import React from "react";
import {
  Skeleton,
  message,
  Descriptions,
  Button,
  notification,
  Tabs,
} from "antd";
import PageLoader from "./PageLoader";
import { gql, useQuery } from "@apollo/client";
import {
  agentTitle,
  lastCheckIn,
  validationCode,
  generateValidationCode,
  removeValidationCode,
  refreshRelayConnection,
  deleteAgent,
} from "./AgentList";
import { Navigate, useParams } from "react-router-dom";
import prettyDateString from "../lib/prettyDateString";
import descriptionList from "../lib/descriptionList";
import { statusIcon, agentStatus } from "../lib/status";
import properCase from "../lib/properCase";

const { TabPane } = Tabs;

const AGENT_QUERY = gql`
  query AgentQuery($agentId: ID!) {
    agent(id: $agentId) {
      id
      name
      createdAt
      updatedAt
      token
      validationCode
      lastCheckInAt
      version
      metadata
    }
  }
`;

function mainAgentDetails(agent): JSX.Element {
  return (
    <div>
      <Descriptions layout="vertical">
        <Descriptions.Item label={<b>Status</b>}>
          {properCase(agentStatus(agent))}
        </Descriptions.Item>
        <Descriptions.Item label={<b>Last Check In At</b>}>
          {lastCheckIn(agent)}
        </Descriptions.Item>
        <Descriptions.Item label={<b>Token</b>}>
          {agent.token}
        </Descriptions.Item>
        <Descriptions.Item label={<b>Validation Code</b>}>
          {validationCode(agent)}
        </Descriptions.Item>
        <Descriptions.Item label={<b>Created At</b>}>
          {prettyDateString(agent.createdAt)}
        </Descriptions.Item>
        <Descriptions.Item label={<b>Updated At</b>}>
          {prettyDateString(agent.updatedAt)}
        </Descriptions.Item>
        <Descriptions.Item label={<b>Version</b>}>
          {agent.version}
        </Descriptions.Item>
      </Descriptions>
    </div>
  );
}

function agentMetadata(agent): JSX.Element {
  return descriptionList(agent.metadata);
}

function agentDetailsTabs(agent): JSX.Element {
  return (
    <Tabs style={{ height: 400 }} defaultActiveKey="1">
      <TabPane tab="Agent Details" key="1">
        {mainAgentDetails(agent)}
      </TabPane>
      <TabPane tab="Computer Metadata" key="2">
        {agentMetadata(agent)}
      </TabPane>
    </Tabs>
  );
}

function AgentDetails(props: any) {
  const { agent_id } = useParams();
  const { loading, error, data } = useQuery(AGENT_QUERY, {
    variables: { agentId: agent_id },
    pollInterval: 1000,
  });

  if (loading) return <PageLoader tip="Loading..." delay={500} />;

  const agent = data.agent;

  if (agent == null) {
    notification["info"]({
      message: "Redirected you to Agent list",
    });
    return <Navigate to="/agents" replace />;
  }

  if (error) {
    message.error("Failed to get Agent Details");
    return <Skeleton />;
  }

  return (
    <div>
      <h1>
        <span style={{ paddingRight: 15 }}>
          {statusIcon(agentStatus(agent), 24)}
        </span>
        {agentTitle(agent)}
      </h1>
      {agentDetailsTabs(agent)}
      <div>
        <Button type="primary" onClick={() => generateValidationCode(agent.id)}>
          Generate Validation Code
        </Button>
        <Button onClick={() => removeValidationCode(agent.id)}>
          Remove Validation Code
        </Button>
        <Button onClick={() => refreshRelayConnection(agent.token)}>
          Refresh Relay Connection
        </Button>
        <Button danger onClick={() => deleteAgent(agent.id)}>
          Delete Agent
        </Button>
      </div>
    </div>
  );
}

export default AgentDetails;
